import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Clock, CheckCircle, XCircle, HelpCircle, Mail } from 'lucide-react';

const RefundPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Refund Policy",
      lastUpdated: "Last Updated: February 21, 2026",
      backHome: "Back to Home",
      sections: [
        {
          icon: RefreshCw,
          title: "Overview",
          content: `At Canada English Life Academy, we are committed to providing high-quality English education. We understand that circumstances may change, and we have established this refund policy to be fair to all our students.

Please read this policy carefully before enrolling in any of our courses or services.`
        },
        {
          icon: CheckCircle,
          title: "Eligible for Refund",
          content: `You may be eligible for a refund in the following cases:

• Course Cancellation by Academy: If we cancel a course before it starts, you will receive a full refund.

• Technical Issues: If persistent technical problems on our end prevent you from accessing course content, and we cannot resolve them within 7 days, you may request a refund.

• Double Payment: If you are charged twice for the same course due to a system error, the duplicate charge will be refunded.

• Within 48 Hours: If you request a refund within 48 hours of purchase and have not accessed more than 10% of the course content.`
        },
        {
          icon: XCircle,
          title: "Not Eligible for Refund",
          content: `Refunds are NOT available in the following situations:

• After 48 hours from purchase date
• If more than 10% of course content has been accessed
• For courses that have already been completed
• For private tutoring sessions that have already taken place
• For free trial or assessment sessions
• If the student violates our Terms of Service
• For promotional or discounted courses (unless specified otherwise)
• For subscription fees after the billing period has started`
        },
        {
          icon: Clock,
          title: "Refund Process & Timeline",
          content: `To request a refund:

1. Contact us at info@canada-englishlife.com within the eligible period
2. Include your order number and reason for refund request
3. Our team will review your request within 3-5 business days
4. If approved, refunds will be processed to your original payment method

Processing Time:
• Credit/Debit Cards: 5-10 business days
• Bank Transfers: 7-14 business days
• Other Payment Methods: Varies by provider

Note: Processing times may vary depending on your financial institution.`
        },
        {
          icon: HelpCircle,
          title: "Special Circumstances",
          content: `We understand that unexpected situations arise. In cases of:
• Medical emergencies (with documentation)
• Family emergencies
• Military deployment

Please contact us directly, and we will work with you to find a fair solution. Each case will be reviewed individually.

Course transfers to another student may be possible in some circumstances - please contact us to discuss options.`
        },
        {
          icon: Mail,
          title: "Contact Us",
          content: `For refund requests or questions about this policy:

Email: info@canada-englishlife.com
Phone: +1 647 874 5620
WhatsApp: +1 647 874 5620

Our customer service team is available:
Monday - Friday: 9:00 AM - 6:00 PM (EST)
Saturday: 10:00 AM - 4:00 PM (EST)

We aim to respond to all inquiries within 24-48 hours.`
        }
      ]
    },
    ar: {
      title: "سياسة الاسترداد",
      lastUpdated: "آخر تحديث: 21 فبراير 2026",
      backHome: "العودة للرئيسية",
      sections: [
        {
          icon: RefreshCw,
          title: "نظرة عامة",
          content: `في أكاديمية Canada English Life، نحن ملتزمون بتقديم تعليم إنجليزي عالي الجودة. نحن نتفهم أن الظروف قد تتغير، وقد وضعنا سياسة الاسترداد هذه لتكون عادلة لجميع طلابنا.

يرجى قراءة هذه السياسة بعناية قبل التسجيل في أي من دوراتنا أو خدماتنا.`
        },
        {
          icon: CheckCircle,
          title: "مؤهل للاسترداد",
          content: `قد تكون مؤهلاً للاسترداد في الحالات التالية:

• إلغاء الدورة من قبل الأكاديمية: إذا ألغينا دورة قبل بدئها، ستحصل على استرداد كامل.

• مشاكل تقنية: إذا منعتك مشاكل تقنية مستمرة من جانبنا من الوصول إلى محتوى الدورة، ولم نتمكن من حلها خلال 7 أيام، يمكنك طلب استرداد.

• دفع مزدوج: إذا تم تحصيل رسوم منك مرتين لنفس الدورة بسبب خطأ في النظام، سيتم استرداد الرسوم المكررة.

• خلال 48 ساعة: إذا طلبت استرداداً خلال 48 ساعة من الشراء ولم تصل إلى أكثر من 10% من محتوى الدورة.`
        },
        {
          icon: XCircle,
          title: "غير مؤهل للاسترداد",
          content: `الاسترداد غير متاح في الحالات التالية:

• بعد 48 ساعة من تاريخ الشراء
• إذا تم الوصول إلى أكثر من 10% من محتوى الدورة
• للدورات التي تم إكمالها بالفعل
• لجلسات التدريس الخاصة التي تمت بالفعل
• للتجارب المجانية أو جلسات التقييم
• إذا انتهك الطالب شروط الخدمة الخاصة بنا
• للدورات الترويجية أو المخفضة (ما لم ينص على خلاف ذلك)
• لرسوم الاشتراك بعد بدء فترة الفوترة`
        },
        {
          icon: Clock,
          title: "عملية الاسترداد والجدول الزمني",
          content: `لطلب استرداد:

1. اتصل بنا على info@canada-englishlife.com خلال الفترة المؤهلة
2. قم بتضمين رقم طلبك وسبب طلب الاسترداد
3. سيراجع فريقنا طلبك خلال 3-5 أيام عمل
4. إذا تمت الموافقة، ستتم معالجة المبالغ المستردة إلى طريقة الدفع الأصلية

وقت المعالجة:
• بطاقات الائتمان/الخصم: 5-10 أيام عمل
• التحويلات البنكية: 7-14 يوم عمل
• طرق الدفع الأخرى: تختلف حسب المزود

ملاحظة: قد تختلف أوقات المعالجة حسب مؤسستك المالية.`
        },
        {
          icon: HelpCircle,
          title: "ظروف خاصة",
          content: `نحن نتفهم أن المواقف غير المتوقعة تحدث. في حالات:
• حالات الطوارئ الطبية (مع الوثائق)
• حالات الطوارئ العائلية
• النشر العسكري

يرجى الاتصال بنا مباشرة، وسنعمل معك لإيجاد حل عادل. سيتم مراجعة كل حالة على حدة.

قد يكون نقل الدورة إلى طالب آخر ممكناً في بعض الظروف - يرجى الاتصال بنا لمناقشة الخيارات.`
        },
        {
          icon: Mail,
          title: "اتصل بنا",
          content: `لطلبات الاسترداد أو الأسئلة حول هذه السياسة:

البريد الإلكتروني: info@canada-englishlife.com
الهاتف: +1 647 874 5620
واتساب: +1 647 874 5620

فريق خدمة العملاء لدينا متاح:
الاثنين - الجمعة: 9:00 صباحاً - 6:00 مساءً (بتوقيت شرق أمريكا)
السبت: 10:00 صباحاً - 4:00 مساءً (بتوقيت شرق أمريكا)

نهدف للرد على جميع الاستفسارات خلال 24-48 ساعة.`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_english-academy-lms/artifacts/fhrsr91w_profile_picture.png"
                alt="Canada English Life Academy"
                className="h-10 w-auto object-contain"
              />
              <span className="text-lg font-bold text-gray-900">Canada English Life</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              {t.backHome}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-500">{t.lastUpdated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 pt-1">{section.title}</h2>
                </div>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>© 2026 Canada English Life Academy. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default RefundPolicy;
